/**
 * Help Modal Component
 * 
 * Displays help content from markdown files in a modal dialog.
 * Uses the existing help system to load and render markdown content.
 */

import React, { useEffect, useRef } from 'react';
import { X, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getHelpArticleBySlug } from '../help/helpArticles';
import { renderMarkdown, parseInline } from '../help/markdownRenderer';

interface HelpModalProps {
  isOpen: boolean;
  onClose: () => void;
  helpSlug: string;
}

export function HelpModal({ isOpen, onClose, helpSlug }: HelpModalProps): React.ReactElement | null {
  const navigate = useNavigate();
  const modalRef = useRef<HTMLDivElement>(null);
  const firstFocusableRef = useRef<HTMLButtonElement>(null);

  const article = getHelpArticleBySlug(helpSlug);

  // Focus management
  useEffect(() => {
    if (isOpen && firstFocusableRef.current) {
      // Small delay to ensure modal is rendered
      setTimeout(() => {
        firstFocusableRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleViewInHelpCenter = () => {
    navigate(`/help/${helpSlug}`);
    onClose();
  };

  // Remove the first paragraph from markdown since it's shown as excerpt in header
  const getMarkdownWithoutFirstParagraph = (markdown: string): string => {
    if (!article?.excerpt) return markdown;
    
    const lines = markdown.split('\n');
    const excerptNormalized = article.excerpt.toLowerCase().trim();
    const result: string[] = [];
    let foundFirstContent = false;
    let firstParagraphStart = -1;
    let firstParagraphEnd = -1;
    let skippedFirstParagraph = false;
    
    // Find the first paragraph (after H1, tags, and horizontal rules)
    for (let i = 0; i < lines.length; i++) {
      const trimmed = lines[i]?.trim() ?? '';
      
      // Skip H1, tags, and initial horizontal rules
      if (trimmed.startsWith('# ') || /^(?:tag|tags):/i.test(trimmed)) {
        continue;
      }
      
      // Skip horizontal rule if it's at the start or right after the first paragraph we're skipping
      if (trimmed === '---') {
        if (!foundFirstContent || skippedFirstParagraph) {
          // Skip horizontal rule at start or after skipped paragraph
          continue;
        }
      }
      
      if (!foundFirstContent && trimmed && !trimmed.startsWith('#') && !trimmed.startsWith('-') && !/^\d+\.\s+/.test(trimmed) && trimmed !== '---') {
        // Found start of first paragraph
        firstParagraphStart = i;
        foundFirstContent = true;
        
        // Find end of paragraph (blank line or next special element)
        for (let j = i; j < lines.length; j++) {
          const nextTrimmed = lines[j]?.trim() ?? '';
          if (!nextTrimmed || nextTrimmed.startsWith('#') || nextTrimmed.startsWith('-') || /^\d+\.\s+/.test(nextTrimmed) || nextTrimmed === '---') {
            firstParagraphEnd = nextTrimmed ? j : j + 1;
            break;
          }
        }
        if (firstParagraphEnd === -1) {
          firstParagraphEnd = lines.length;
        }
        
        // Check if this paragraph matches the excerpt
        const paragraphText = lines.slice(firstParagraphStart, firstParagraphEnd)
          .map(l => l.trim())
          .filter(l => l)
          .join(' ')
          .toLowerCase()
          .trim();
        
        const excerptWords = excerptNormalized.split(/\s+/).filter(w => w.length > 2);
        const matchingWords = excerptWords.filter(word => paragraphText.includes(word));
        
        // If at least 40% of excerpt words match, skip this paragraph
        if (matchingWords.length >= Math.max(3, excerptWords.length * 0.4)) {
          // Skip this paragraph
          skippedFirstParagraph = true;
          i = firstParagraphEnd - 1; // Continue from after the paragraph
          // Also skip the next horizontal rule if it exists
          if (i < lines.length - 1 && lines[i + 1]?.trim() === '---') {
            i++; // Skip the horizontal rule
          }
          continue;
        }
      }
      
      result.push(lines[i] ?? '');
    }
    
    return result.join('\n');
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[70] overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="help-modal-title"
      onClick={handleBackdropClick}
    >
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />

      {/* Modal */}
      <div className="flex min-h-full items-start justify-center pt-14 pb-4 px-4">
        <div
          ref={modalRef}
          className="relative w-full max-w-3xl transform overflow-hidden rounded-lg bg-white shadow-xl transition-all dark:bg-gray-900"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
            <div className="flex-1">
              <h1 id="help-modal-title" className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {article?.title ?? 'Help'}
              </h1>
              {article?.excerpt && (
                <p className="mt-2 text-base text-gray-600 dark:text-gray-400">
                  {parseInline(article.excerpt)}
                </p>
              )}
            </div>
            <button
              ref={firstFocusableRef}
              onClick={onClose}
              className="ml-4 p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              aria-label="Close help modal"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="max-h-[calc(100vh-12rem)] overflow-y-auto px-6 py-4">
            {article ? (
              <div className="prose prose-sm max-w-none dark:prose-invert">
                {renderMarkdown(getMarkdownWithoutFirstParagraph(article.markdown))}
              </div>
            ) : (
              <div className="py-8 text-center">
                <p className="text-gray-600 dark:text-gray-400">
                  Help content not found for this page.
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between border-t border-gray-200 px-6 py-4 dark:border-gray-700">
            <button
              onClick={handleViewInHelpCenter}
              className="btn-primary btn-sm inline-flex items-center gap-2"
            >
              <ExternalLink className="h-4 w-4" aria-hidden="true" />
              View in Help Center
            </button>
            <button
              onClick={onClose}
              className="btn-secondary btn-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


